import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DetailedResultsViewReportButtonRow,
  DetailedResultsViewReportButtonWrapper,
  DetailedResultsViewReportWarning,
  DetailedResultsViewReportWrapper,
} from "./DetailedResults.styles";
import { StyledButton } from "../shared/Button/Button.styles";
import { ShowReportIcon } from "../../assets/iconComponents/ShowReport";
import { Warning } from "../../assets/iconComponents/Warning";
import { GetTypographyContent } from "../shared/Typography/Typography";
import { SpanishReportButton } from "../shared/SpanishReportButton/SpanishReportButton";
import { FetalSexModalState } from "./DetailedResults.types";

export const DetailedResultsViewReport: React.FC<{
  fetalSexWarning?: boolean;
  /** Triggers report download. Should handle URL fetching, analytics, and download. */
  reportDownload?: () => Promise<void>;
  /** Triggers Spanish report download. Should handle URL fetching, analytics, and download. */
  spanishReportDownload?: () => Promise<void>;
  setShowFetalSexModal?: (show: FetalSexModalState) => void;
}> = ({
  setShowFetalSexModal,
  reportDownload,
  spanishReportDownload,
  fetalSexWarning,
}) => {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const [downloadingSpanish, setDownloadingSpanish] = useState(false);

  const ViewReportButton = (
    <StyledButton
      size="small"
      $backgroundColor="redMedium"
      $textColor="white"
      data-testid="view-full-report"
      onClick={async () => {
        if (setShowFetalSexModal && fetalSexWarning) {
          setShowFetalSexModal("report");
        } else if (!downloading && reportDownload) {
          setDownloading(true);
          try {
            await reportDownload();
          } catch (error) {
            console.error("Failed to download report:", error);
          } finally {
            setDownloading(false);
          }
        }
      }}
    >
      <DetailedResultsViewReportButtonWrapper>
        <div>
          <ShowReportIcon color="white" />
        </div>
        <div>
          {GetTypographyContent({
            content: t("results.actions.viewFullReport"),
            size: "medium",
            textColor: "white",
            isBold: true,
            isCaps: true,
          })}
        </div>
      </DetailedResultsViewReportButtonWrapper>
    </StyledButton>
  );

  const ViewSpanishReportButton = spanishReportDownload ? (
    <SpanishReportButton
      onClick={async () => {
        if (setShowFetalSexModal && fetalSexWarning) {
          setShowFetalSexModal("report_es");
        } else if (!downloadingSpanish) {
          setDownloadingSpanish(true);
          try {
            await spanishReportDownload();
          } catch (error) {
            console.error("Failed to download Spanish report:", error);
          } finally {
            setDownloadingSpanish(false);
          }
        }
      }}
      isDownloading={downloadingSpanish}
      testId="view-spanish-report"
    />
  ) : null;

  return (
    <DetailedResultsViewReportWrapper>
      <>
        <DetailedResultsViewReportButtonRow>
          {ViewReportButton}
          {ViewSpanishReportButton}
        </DetailedResultsViewReportButtonRow>
        {fetalSexWarning && (
          <DetailedResultsViewReportWarning>
            <div>
              <Warning />
            </div>
            <div>
              {GetTypographyContent({
                content: t("results.detail.resultsContainSex"),
                size: "medium",
                textColor: "redDark",
              })}
            </div>
          </DetailedResultsViewReportWarning>
        )}
      </>
    </DetailedResultsViewReportWrapper>
  );
};
